import { ClientIdentity } from "@shared/schema";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PlusIcon, Calendar, FileText, GlobeIcon } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatDate } from "@/lib/utils";
import { InlineEditableField } from "@/components/InlineEditableField";

type IdentityInfoPanelProps = {
  identities: ClientIdentity[];
  clientId: number;
  editMode?: boolean;
  onDraftChange?: (id: number, updates: Partial<ClientIdentity>) => void;
};

export function IdentityInfoPanel({ identities, clientId, editMode = false, onDraftChange }: IdentityInfoPanelProps) {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [selectedIdentityId, setSelectedIdentityId] = useState<number | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const addIdentity = useMutation({
    mutationFn: async () => {
      const created = await apiRequest('POST', '/api/client-identities', {
        clientId,
        type: 'Other'
      });
      return created;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [`/api/contacts/${clientId}/identities`] });
      toast({ title: 'Identity added' });
    },
    onError: () => toast({ title: 'Failed to add identity', variant: 'destructive' })
  });

  const patch = async (id: number, updates: Partial<ClientIdentity>) => {
    await apiRequest('PATCH', `/api/client-identities/${id}`, updates);
    await queryClient.invalidateQueries({ queryKey: [`/api/contacts/${clientId}/identities`] });
  };

  // Get the selected identity
  const selectedIdentity = selectedIdentityId 
    ? identities.find(id => id.id === selectedIdentityId) 
    : null;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Identity Documents</h3>
        <Button size="sm" onClick={() => addIdentity.mutate()} disabled={addIdentity.isPending}>
          <PlusIcon className="h-4 w-4 mr-2" />
          {addIdentity.isPending ? 'Adding…' : 'Add Document'}
        </Button>
      </div>

      {identities.length === 0 ? (
        <Alert>
          <AlertDescription>
            No identity documents have been added for this contact yet.
          </AlertDescription>
        </Alert>
      ) : (
        <Tabs 
          defaultValue={identities[0].id.toString()}
          onValueChange={(value) => setSelectedIdentityId(parseInt(value))}
          className="w-full"
        >
          <TabsList className="grid grid-flow-col gap-2 overflow-x-auto border-b w-full rounded-none justify-start pb-2">
            {identities.map((identity) => (
              <TabsTrigger 
                key={identity.id} 
                value={identity.id.toString()}
                className="data-[state=active]:border-primary data-[state=active]:shadow-none border-b-2 border-transparent rounded-none"
              >
                <div className="flex items-center gap-1">
                  <span>{identity.type}</span>
                  <Badge 
                    variant={
                      identity.expirationDate && new Date(identity.expirationDate) < new Date() 
                        ? "destructive" 
                        : "secondary"
                    }
                    className="text-xs"
                  >
                    {identity.issuingCountry}
                  </Badge>
                </div>
              </TabsTrigger>
            ))}
          </TabsList>
          
          {identities.map((identity) => (
            <TabsContent key={identity.id} value={identity.id.toString()}>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex justify-between items-start mb-4">
                    <div className="space-y-1">
                      <h4 className="font-semibold">{identity.type}</h4>
                      {identity.documentNumber && (
                        <p className="text-sm text-muted-foreground">
                          Document No: {identity.documentNumber}
                        </p>
                      )}
                    </div>
                    
                    <Button variant="outline" size="sm">
                      Edit
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InlineEditableField
                      label="Issuing Country"
                      value={identity.issuingCountry}
                      placeholder="Country"
                      forceEdit={false}
                      onSave={async (val) => patch(identity.id!, { issuingCountry: val })}
                      bulkEdit={editMode}
                      onChange={(val) => onDraftChange?.(identity.id!, { issuingCountry: val })}
                    />
                    <InlineEditableField
                      label="Document Number"
                      value={identity.documentNumber}
                      placeholder="Document Number"
                      forceEdit={false}
                      onSave={async (val) => patch(identity.id!, { documentNumber: val })}
                      bulkEdit={editMode}
                      onChange={(val) => onDraftChange?.(identity.id!, { documentNumber: val })}
                    />
                    <InlineEditableField
                      label="Issue Date"
                      value={identity.issueDate || ''}
                      placeholder="YYYY-MM-DD"
                      forceEdit={false}
                      onSave={async (val) => patch(identity.id!, { issueDate: val })}
                      bulkEdit={editMode}
                      onChange={(val) => onDraftChange?.(identity.id!, { issueDate: val })}
                    />
                    <InlineEditableField
                      label="Expiration Date"
                      value={identity.expirationDate || ''}
                      placeholder="YYYY-MM-DD"
                      forceEdit={false}
                      onSave={async (val) => patch(identity.id!, { expirationDate: val })}
                      bulkEdit={editMode}
                      onChange={(val) => onDraftChange?.(identity.id!, { expirationDate: val })}
                    />
                  </div>
                  <InlineEditableField
                    label="Notes"
                    value={identity.notes}
                    placeholder="Notes"
                    forceEdit={false}
                    onSave={async (val) => patch(identity.id!, { notes: val })}
                    bulkEdit={editMode}
                    onChange={(val) => onDraftChange?.(identity.id!, { notes: val })}
                  />
                </CardContent>
              </Card>
            </TabsContent>
          ))}
        </Tabs>
      )}

      {/* Add Document Dialog - To be implemented */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Identity Document</DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              Form will be implemented here to add new identity documents like passport, visa, etc.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* TODO: Add edit dialog */}
    </div>
  );
}
